import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button, Link } from "capitalrx-components-2.0/lib/general";
import { Body, Title } from "capitalrx-components-2.0/lib/typography";

import { PASearchForm, PAListForm } from "@/components";
import { CaseSearchErrors, STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL } from "@/utils";

import styles from "./PASearchPage.module.scss";

export const PASearchPage: FC = () => {
  const navigate = useNavigate();

  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);

  const { isUserLoggedIn, getUser } = useContext(AuthenticationContext);
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const [activeProvider, setActiveProvider] = useState<boolean>(false);

  useEffect(() => {
    // Not logged in, show lookup only
    if (!isUserLoggedIn()) {
      setActiveProvider(false);
      return;
    }

    // If logged in and user has active pm record, show list also
    const checkUser = async () => {
      const user = await getUser();
      setActiveProvider(user.provider_enrollment_status === "active");
    };
    checkUser();

  }, [isUserLoggedIn, getUser]);

  const [selectedTab, setSelectedTab] = useState<"list" | "lookup">("list");

  const delectedToggleStyle = {
    backgroundColor: "transparent",
    border: "1px solid transparent",
    color: "#313E48",
  };

  const logPACreateClick = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.new);
  }, []);

  const handleListClick = useCallback(() => setSelectedTab("list"), []);
  const handleLookupClick = useCallback(() => setSelectedTab("lookup"), []);

  const handleNewCaseClick = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.new);
    navigate(getThemeURL("PA_CREATE"));
  }, []);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get("error");

    if (error === CaseSearchErrors.NOT_FOUND) {
      setSelectedTab("lookup");
    }
  }, [searchParams]);


  return (
    <div className={styles.PAPanelContainer}>
      {activeProvider && (
        <Stack>
          <Stack spacing="small">
            <Layout>
              <Title className={styles.CaseSearchCenterText} testId="test__prior_authorization_search_heading">Prior Authorizations</Title>
              <Shelf.Spacer />
              <Stack alignment="left">
                <Button
                  appearance={getThemeButton("secondary", primary_color, secondary_hover_color)}
                  onClick={handleNewCaseClick}
                >Start A New Case</Button>
              </Stack>
            </Layout>
            <Body className={styles.CaseSearchCenterText} testId="test__prior_authorization_search_description">Find an existing case to add documents/check the status, or start a new case</Body>
          </Stack>
          <Grid className={styles.PAToggleBar}>
            <Grid.Span columns={6}>
              <Button
                onClick={handleListClick}
                appearance={getThemeButton("primary", primary_color, primary_hover_color)}
                style={selectedTab === "list" ? {} : delectedToggleStyle}
              >
                Associated Cases List
              </Button>
            </Grid.Span>
            <Grid.Span columns={6}>
              <Button
                onClick={handleLookupClick}
                appearance={getThemeButton("primary", primary_color, primary_hover_color)}
                style={selectedTab === "lookup" ? {} : delectedToggleStyle}
              >
                Case Lookup
              </Button>
            </Grid.Span>
          </Grid>
          {selectedTab === "list" && <PAListForm />}
          {selectedTab === "lookup" && <PASearchForm setSelectedTab={setSelectedTab} />}
        </Stack>
      )}
      {!activeProvider && (
        <Stack spacing="large" style={{ paddingBottom: "16px" }}>
          <PASearchForm showTitle={true}/>
          <Link
            className={styles.PAPageCenterText}
            style={{ color: primary_color }}
            onClick={logPACreateClick}
            to={getThemeURL("PA_CREATE")}
            testId="test__prior_authorization_search_page_new_case_link"
          >Start a new case</Link>
        </Stack>
      )}
    </div>
  );
};

export default PASearchPage;
